import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "../components/ui/table";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../components/ui/select";
import {
    ScrollText,
    Loader2,
    RefreshCw,
    Upload,
    UserCheck,
    Trash2,
    Search,
    Activity,
    Eraser,
    Download,
    Eye,
} from "lucide-react";
import { toast } from "sonner";
import { formatBytes } from "../lib/documents";
import { useSortableTable } from "../hooks/useSortableTable";
import { SortableHead } from "../components/SortableHead";
import { DocumentPreviewDialog } from "../components/DocumentPreviewDialog";
import { Checkbox } from "../components/ui/checkbox";

const fmtDateTime = (iso) => {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        return d.toLocaleString("it-IT", {
            day: "2-digit", month: "2-digit", year: "numeric",
            hour: "2-digit", minute: "2-digit",
        });
    } catch {
        return iso;
    }
};

const TYPE_OPTIONS = [
    { value: "all", label: "Tutti i tipi" },
    { value: "upload", label: "Caricamento" },
    { value: "assign", label: "Assegnazione" },
    { value: "delete", label: "Eliminazione" },
    { value: "download", label: "Download" },
];

const KIND_BADGE = {
    upload: { color: "bg-blue-100 text-blue-700", icon: Upload, label: "Caricato" },
    assign: { color: "bg-emerald-100 text-emerald-700", icon: UserCheck, label: "Assegnato" },
    delete: { color: "bg-red-100 text-red-700", icon: Trash2, label: "Eliminato" },
    download: { color: "bg-violet-100 text-violet-700", icon: Download, label: "Scaricato" },
};

const downloadEventsCsv = (events) => {
    const header = "Data,Tipo,File,Categoria,Dipendente,Matricola,Gruppo,Operatore,Dimensione\n";
    const rows = events.map((e) => [
        fmtDateTime(e.at),
        KIND_BADGE[e.kind]?.label || e.kind,
        e.filename || "",
        e.category_label || "",
        e.employee_label || "",
        e.matricola || "",
        e.group_name || "",
        e.actor_email || "",
        e.size_bytes || 0,
    ].map((v) => `"${String(v).replace(/"/g, '""')}"`).join(","));
    const blob = new Blob([header + rows.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `storico_documenti_${new Date().toISOString().slice(0, 19).replace(/:/g, "-")}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
};

export default function DocumentAuditPage() {
    const [events, setEvents] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [filterType, setFilterType] = useState("all");
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");
    const [search, setSearch] = useState("");
    const [limit, setLimit] = useState(200);
    const [total, setTotal] = useState(0);
    const [previewDoc, setPreviewDoc] = useState(null);
    const [deletingId, setDeletingId] = useState(null);
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [bulkDeleting, setBulkDeleting] = useState(false);

    const fetchData = async () => {
        setLoading(true);
        try {
            const params = {};
            if (filterType && filterType !== "all") params.type = filterType;
            if (dateFrom) params.date_from = dateFrom;
            if (dateTo) params.date_to = dateTo;
            if (search.trim()) params.search = search.trim();
            params.limit = limit;
            const [logsRes, statsRes] = await Promise.all([
                api.get("/documents/audit-log", { params }),
                api.get("/documents/audit-log/stats"),
            ]);
            setEvents(logsRes.data.events || []);
            setTotal(logsRes.data.total || 0);
            setStats(statsRes.data);
            setSelectedIds(new Set());
        } catch (e) {
            toast.error("Errore nel caricamento dello storico");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const guessType = (name) => {
        const n = (name || "").toLowerCase();
        if (n.endsWith(".pdf")) return "application/pdf";
        if (n.endsWith(".png")) return "image/png";
        if (n.endsWith(".jpg") || n.endsWith(".jpeg")) return "image/jpeg";
        if (n.endsWith(".webp")) return "image/webp";
        return "";
    };

    const handleDeleteDoc = async (ev) => {
        if (!window.confirm(`Eliminare "${ev.filename}" dal portale?\nIl documento non sarà più visibile né scaricabile.`)) return;
        setDeletingId(ev.document_id);
        try {
            await api.delete(`/documents/${ev.document_id}`);
            toast.success("Documento eliminato");
            fetchData();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Errore eliminazione");
        } finally {
            setDeletingId(null);
        }
    };

    const toggleSelect = (docId) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(docId)) next.delete(docId);
            else next.add(docId);
            return next;
        });
    };

    const handleBulkDelete = async () => {
        const ids = [...selectedIds];
        if (!ids.length) return;
        if (!window.confirm(`Eliminare ${ids.length} ${ids.length === 1 ? "documento" : "documenti"} dal portale?\nNon saranno più visibili né scaricabili.`)) return;
        setBulkDeleting(true);
        try {
            const { data } = await api.post("/documents/bulk-delete", { document_ids: ids });
            toast.success(`${data.deleted} ${data.deleted === 1 ? "documento eliminato" : "documenti eliminati"}`);
            fetchData();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Errore eliminazione multipla");
        } finally {
            setBulkDeleting(false);
        }
    };

    const clearFilters = () => {
        setFilterType("all");
        setDateFrom("");
        setDateTo("");
        setSearch("");
        setLimit(200);
    };

    const truncated = total > events.length;

    const { sortedItems: eventsSorted, sortKey, sortDir, requestSort } = useSortableTable(events, {
        customAccessors: {
            tipo: (e) => e.kind || "",
            dipendente: (e) => e.employee_label || e.group_name || "",
            file: (e) => (e.filename || "").toLowerCase(),
        },
    });

    const selectableIds = useMemo(
        () => [...new Set(eventsSorted.filter((e) => !e.is_deleted).map((e) => e.document_id))],
        [eventsSorted]
    );

    const statCards = useMemo(() => {
        if (!stats) return null;
        return [
            { label: "Ultime 24h", data: stats.last_24h, color: "bg-blue-50 border-blue-200 text-blue-900", icon: Activity },
            { label: "Ultimi 7 giorni", data: stats.last_7d, color: "bg-emerald-50 border-emerald-200 text-emerald-900", icon: Activity },
            { label: "Ultimi 30 giorni", data: stats.last_30d, color: "bg-violet-50 border-violet-200 text-violet-900", icon: Activity },
        ];
    }, [stats]);

    return (
        <div data-testid="document-audit-page">
            <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
                    <div className="flex items-center gap-3">
                        <div className="flex h-12 w-12 items-center justify-center rounded-md bg-[#376DB3]/10 text-[#376DB3]">
                            <ScrollText className="h-6 w-6" />
                        </div>
                        <div>
                            <h1 className="font-display text-2xl font-bold text-[#376DB3]">Storico operazioni documenti</h1>
                            <p className="text-sm text-slate-600">
                                Tutte le azioni eseguite sui documenti: caricamenti, assegnazioni e eliminazioni.
                            </p>
                        </div>
                    </div>
                    <div className="flex gap-2">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => downloadEventsCsv(events)}
                            disabled={!events.length}
                            className="gap-2"
                            data-testid="audit-export-csv"
                        >
                            <Download className="h-4 w-4" />
                            Esporta CSV ({events.length})
                        </Button>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={fetchData}
                            disabled={loading}
                            className="gap-2"
                            data-testid="audit-refresh-btn"
                        >
                            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
                            Aggiorna
                        </Button>
                    </div>
                </div>

                {/* Stat cards */}
                <div className={`mb-6 grid gap-3 sm:grid-cols-3 ${statCards ? "" : "hidden"}`} data-testid="audit-stat-cards">
                    {(statCards || []).map((c) => {
                        const Icon = c.icon;
                        return (
                            <div key={c.label} className={`rounded-lg border p-4 ${c.color}`}>
                                <div className="flex items-center justify-between">
                                    <div className="font-display text-xs font-semibold uppercase tracking-wide opacity-80">{c.label}</div>
                                    <Icon className="h-4 w-4 opacity-60" />
                                </div>
                                <div className="mt-2 flex items-end gap-4 text-sm">
                                    <div>
                                        <div className="font-mono-numbers text-2xl font-bold">{c.data?.total ?? 0}</div>
                                        <div className="text-xs opacity-70">Totale eventi</div>
                                    </div>
                                    <div className="ml-auto grid gap-1 text-right text-xs">
                                        <span>↑ {c.data?.upload ?? 0} caricati</span>
                                        <span>✓ {c.data?.assign ?? 0} assegnati</span>
                                        <span>✗ {c.data?.delete ?? 0} eliminati</span>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Filters */}
                <div className="mb-4 rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
                    <div className="grid gap-3 sm:grid-cols-[180px_180px_180px_1fr_auto]">
                        <div>
                            <Label className="text-xs text-slate-600">Tipo evento</Label>
                            <Select value={filterType} onValueChange={setFilterType}>
                                <SelectTrigger className="mt-1 h-9" data-testid="audit-filter-type">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {TYPE_OPTIONS.map((o) => (
                                        <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-xs text-slate-600">Dal</Label>
                            <Input
                                type="date"
                                value={dateFrom}
                                onChange={(e) => setDateFrom(e.target.value)}
                                className="mt-1 h-9"
                                data-testid="audit-filter-from"
                            />
                        </div>
                        <div>
                            <Label className="text-xs text-slate-600">Al</Label>
                            <Input
                                type="date"
                                value={dateTo}
                                onChange={(e) => setDateTo(e.target.value)}
                                className="mt-1 h-9"
                                data-testid="audit-filter-to"
                            />
                        </div>
                        <div>
                            <Label className="text-xs text-slate-600">Cerca per nome file</Label>
                            <div className="relative mt-1">
                                <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                                <Input
                                    placeholder="es. busta-paga-2024-03.pdf"
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === "Enter") fetchData(); }}
                                    className="h-9 pl-8"
                                    data-testid="audit-filter-search"
                                />
                            </div>
                        </div>
                        <div className="flex items-end gap-2">
                            <Button
                                size="sm"
                                onClick={fetchData}
                                disabled={loading}
                                className="h-9 gap-2 bg-[#376DB3] text-white hover:bg-[#376DB3]/90"
                                data-testid="audit-apply-btn"
                            >
                                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                                Applica
                            </Button>
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => { clearFilters(); }}
                                className="h-9 gap-1 text-slate-600"
                                data-testid="audit-clear-btn"
                            >
                                <Eraser className="h-4 w-4" />
                                Pulisci
                            </Button>
                        </div>
                    </div>
                </div>

                {/* Truncated banner */}
                <div className={`mb-3 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 ${truncated ? "" : "hidden"}`} data-testid="audit-truncated-banner">
                    Mostrati i primi <b>{events.length}</b> eventi su <b>{total}</b>. Affina i filtri o aumenta il limite per vederne di più.
                    <div className="mt-2 flex items-center gap-2">
                        <Label className="text-xs">Limite:</Label>
                        <Select value={String(limit)} onValueChange={(v) => { setLimit(Number(v)); }}>
                            <SelectTrigger className="h-7 w-28 text-xs" data-testid="audit-limit-select">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {[100, 200, 500, 1000].map((n) => (
                                    <SelectItem key={n} value={String(n)}>{n}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <Button size="sm" onClick={fetchData} className="h-7 bg-amber-600 px-3 text-xs text-white hover:bg-amber-700">
                            Ricarica
                        </Button>
                    </div>
                </div>

                {/* Table */}
                <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
                    {selectedIds.size > 0 && (
                        <div className="flex flex-wrap items-center gap-3 border-b border-red-200 bg-red-50 px-4 py-2" data-testid="bulk-delete-bar">
                            <span className="text-sm font-medium text-red-800">
                                {selectedIds.size} {selectedIds.size === 1 ? "documento selezionato" : "documenti selezionati"}
                            </span>
                            <Button
                                size="sm"
                                onClick={handleBulkDelete}
                                disabled={bulkDeleting}
                                className="h-7 gap-1.5 bg-red-600 px-2.5 text-xs text-white hover:bg-red-700"
                                data-testid="bulk-delete-btn"
                            >
                                {bulkDeleting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                                Elimina selezionati
                            </Button>
                            <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => setSelectedIds(new Set())}
                                className="h-7 px-2 text-xs text-slate-600"
                                data-testid="bulk-clear-btn"
                            >
                                Deseleziona tutto
                            </Button>
                        </div>
                    )}
                    <Table>
                        <TableHeader>
                            <TableRow className="bg-slate-50 hover:bg-slate-50">
                                <TableHead className="w-10">
                                    <Checkbox
                                        checked={selectableIds.length > 0 && selectableIds.every((id) => selectedIds.has(id))
                                            ? true
                                            : selectedIds.size > 0
                                              ? "indeterminate"
                                              : false}
                                        onCheckedChange={(v) => setSelectedIds(v ? new Set(selectableIds) : new Set())}
                                        disabled={selectableIds.length === 0}
                                        data-testid="bulk-select-all"
                                    />
                                </TableHead>
                                <SortableHead sortKey="at" sortKeyCurrent={sortKey} sortDir={sortDir} onClick={requestSort} className="w-44">
                                    Data
                                </SortableHead>
                                <SortableHead sortKey="tipo" sortKeyCurrent={sortKey} sortDir={sortDir} onClick={requestSort} className="w-32">
                                    Tipo
                                </SortableHead>
                                <SortableHead sortKey="file" sortKeyCurrent={sortKey} sortDir={sortDir} onClick={requestSort}>
                                    File
                                </SortableHead>
                                <SortableHead sortKey="dipendente" sortKeyCurrent={sortKey} sortDir={sortDir} onClick={requestSort}>
                                    Dipendente / Gruppo
                                </SortableHead>
                                <SortableHead sortKey="actor_email" sortKeyCurrent={sortKey} sortDir={sortDir} onClick={requestSort}>
                                    Operatore
                                </SortableHead>
                                <SortableHead sortKey="size_bytes" sortKeyCurrent={sortKey} sortDir={sortDir} onClick={requestSort} className="text-right">
                                    Dimensione
                                </SortableHead>
                                <TableHead className="text-right">Azioni</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={8} className="py-12 text-center">
                                        <Loader2 className="mx-auto h-6 w-6 animate-spin text-slate-400" />
                                    </TableCell>
                                </TableRow>
                            ) : eventsSorted.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={8} className="py-12 text-center text-sm text-slate-500">
                                        <ScrollText className="mx-auto mb-2 h-10 w-10 text-slate-300" />
                                        Nessun evento corrisponde ai filtri scelti.
                                    </TableCell>
                                </TableRow>
                            ) : (
                                eventsSorted.map((e, idx) => {
                                    const meta = KIND_BADGE[e.kind] || KIND_BADGE.upload;
                                    const Icon = meta.icon;
                                    return (
                                        <TableRow key={`${e.document_id}-${e.kind}-${idx}`} data-testid={`audit-row-${e.document_id}-${e.kind}`}>
                                            <TableCell>
                                                <Checkbox
                                                    checked={selectedIds.has(e.document_id)}
                                                    onCheckedChange={() => toggleSelect(e.document_id)}
                                                    disabled={e.is_deleted}
                                                    data-testid={`bulk-select-${e.document_id}-${e.kind}`}
                                                />
                                            </TableCell>
                                            <TableCell className="font-mono-numbers text-xs text-slate-700">
                                                {fmtDateTime(e.at)}
                                            </TableCell>
                                            <TableCell>
                                                <Badge className={`gap-1 ${meta.color}`} variant="secondary">
                                                    <Icon className="h-3 w-3" />
                                                    {meta.label}
                                                </Badge>
                                            </TableCell>
                                            <TableCell>
                                                <div className="font-medium text-slate-800" title={e.filename}>
                                                    {e.filename || "—"}
                                                </div>
                                                <div className="mt-0.5 flex flex-wrap gap-1 text-xs">
                                                    <span className="text-slate-500">{e.category_label}</span>
                                                    <span className={`text-amber-600 ${e.is_pending && !e.is_deleted ? "" : "hidden"}`}>· in attesa</span>
                                                    <span className={`text-red-600 ${e.is_deleted ? "" : "hidden"}`}>· cancellato</span>
                                                    {e.kind === "download" && e.download_meta?.legacy_import && (
                                                        <span
                                                            className="inline-flex items-center rounded-sm border border-amber-300 bg-amber-50 px-1 text-[10px] font-semibold uppercase tracking-wide text-amber-700"
                                                            title="Download importato dal vecchio portale (pre-eIDAS)"
                                                        >
                                                            legacy
                                                        </span>
                                                    )}
                                                    {e.kind === "download" && e.download_meta?.acknowledged && (
                                                        <span className="inline-flex items-center rounded-sm border border-emerald-300 bg-emerald-50 px-1 text-[10px] font-semibold uppercase tracking-wide text-emerald-700">
                                                            presa visione
                                                        </span>
                                                    )}
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                {e.employee_id ? (
                                                    <div>
                                                        <Link
                                                            to={`/dipendenti/${e.employee_id}`}
                                                            className="font-medium text-[#376DB3] hover:underline"
                                                            data-testid={`audit-emp-link-${e.document_id}`}
                                                        >
                                                            {e.employee_label || "Dipendente"}
                                                        </Link>
                                                        <div className="text-xs text-slate-500">
                                                            <span className={e.matricola ? "" : "hidden"}>matr. {e.matricola}</span>
                                                            <span className={e.employee_cessato ? "ml-2 font-medium text-amber-600" : "hidden"}>CESSATO</span>
                                                        </div>
                                                    </div>
                                                ) : e.group_name ? (
                                                    <span
                                                        className="inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium text-white"
                                                        style={{ background: e.group_color || "#376DB3" }}
                                                    >
                                                        Gruppo {e.group_name}
                                                    </span>
                                                ) : (
                                                    <span className="text-xs italic text-slate-400">— non assegnato —</span>
                                                )}
                                            </TableCell>
                                            <TableCell className="text-xs text-slate-700">
                                                {e.actor_email || <span className="italic text-slate-400">sistema</span>}
                                            </TableCell>
                                            <TableCell className="text-right font-mono-numbers text-xs text-slate-600">
                                                {formatBytes(e.size_bytes || 0)}
                                            </TableCell>
                                            <TableCell className="text-right">
                                                <div className="flex items-center justify-end gap-1">
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        className="h-7 w-7 text-slate-500 hover:text-[#376DB3]"
                                                        disabled={e.is_deleted}
                                                        onClick={() => setPreviewDoc({ id: e.document_id, filename: e.filename, content_type: guessType(e.filename) })}
                                                        title={e.is_deleted ? "Documento eliminato" : "Anteprima documento"}
                                                        data-testid={`audit-preview-btn-${e.document_id}`}
                                                    >
                                                        <Eye className="h-3.5 w-3.5" />
                                                    </Button>
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        className="h-7 w-7 text-slate-500 hover:text-red-600"
                                                        disabled={e.is_deleted || deletingId === e.document_id}
                                                        onClick={() => handleDeleteDoc(e)}
                                                        title={e.is_deleted ? "Già eliminato" : "Elimina dal portale"}
                                                        data-testid={`audit-delete-btn-${e.document_id}`}
                                                    >
                                                        {deletingId === e.document_id
                                                            ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                                            : <Trash2 className="h-3.5 w-3.5" />}
                                                    </Button>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                </div>

                <div className="mt-3 text-xs text-slate-500">
                    Mostrati <b>{events.length}</b> {total > 0 && total !== events.length ? `di ${total} eventi totali` : `eventi`}.
                </div>

                <DocumentPreviewDialog open={!!previewDoc} document={previewDoc} onClose={() => setPreviewDoc(null)} />
            </main>
        </div>
    );
}
